/**
 * Sign in — §7.6's password + TOTP.
 *
 * The TOTP field is always shown rather than appearing after the server asks
 * for it. Every admin is enrolled at creation (`createAdmin` mints the secret),
 * so a two-step form would add a round trip to tell people something that is
 * always true, and a code field that appears late is one people paste into
 * after their code has already rolled over.
 */

import { useState } from 'react';
import { api, ApiError } from '../api/client.ts';
import { setToken, setTrustedDevice } from '../auth.ts';
import { Banner, Field } from '../components/ui.tsx';
import type { AdminView } from '../api/types.ts';

export function Login({ onSignedIn }: { onSignedIn: (admin: AdminView) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [enrolmentCode, setEnrolmentCode] = useState('');
  const [showEnrolment, setShowEnrolment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const session = await api.login(
        username.trim(),
        password,
        totpCode.trim(),
        enrolmentCode.trim() || undefined,
      );
      setToken(session.token, session.expiresAt);
      if (session.trustedDevice) setTrustedDevice(session.trustedDevice);
      onSignedIn(session.admin);
    } catch (err) {
      setError(messageFor(err));
      // Never the username, and never on failure: a form that clears only the
      // password lets someone retry a typo'd code without retyping everything,
      // and someone brute-forcing has learned nothing either way.
      setPassword('');
      setTotpCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form
        className="login__card"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h1 className="login__title">Bingo admin</h1>

        <Field label="Username">
          <input
            className="input"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </Field>

        <Field label="Password">
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Field label="Authenticator code" hint="Six digits from your authenticator app">
          <input
            className="input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
          />
        </Field>

        {showEnrolment ? (
          <Field
            label="Enrolment code"
            hint="First time on this network? Enter the one-time code minted from the allowed console."
          >
            <input
              className="input"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="XXXX-XXXX-XXXX"
              value={enrolmentCode}
              onChange={(e) => setEnrolmentCode(e.target.value.toUpperCase())}
            />
          </Field>
        ) : (
          <button
            type="button"
            className="login__bootstrap"
            onClick={() => setShowEnrolment(true)}
          >
            First time on this network? Use a one-time enrolment code.
          </button>
        )}

        {error ? <Banner kind="error">{error}</Banner> : null}

        <button
          type="submit"
          className="button button--primary button--wide"
          disabled={busy || !username || !password || totpCode.length !== 6}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

/**
 * The one place in this console that does *not* show the server's message
 * verbatim.
 *
 * Everywhere else the server is talking to an operator about a workflow. Here
 * it is talking to whoever filled in the form, who may not be the account
 * holder — so "invalid credentials" stays deliberately vague about which half
 * was wrong. The two exceptions are states the real owner has to be told
 * about: a locked account (§7.6 locks after five failures, and being locked out
 * is itself the signal that someone is trying) and a suspended one, where
 * retrying will never help.
 */
function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Something went wrong.';
  switch (err.code) {
    case 'account_locked':
      return 'This account is locked after too many failed attempts. Contact a superadmin.';
    case 'account_not_active':
      return 'This account is not active.';
    case 'totp_required':
      return 'Enter the six-digit code from your authenticator app.';
    case 'rate_limited':
      return err.message;
    case 'not_found':
      // §7.6's IP allowlist answers 404 rather than 403, so the admin surface
      // is not confirmed to exist. From here that is indistinguishable from a
      // missing route, and the honest thing to say is that this network cannot
      // reach it — and, since this is the one state the operator's bootstrap
      // path exists for, what the way in is.
      return 'The admin API is not reachable from this network. Use the enrolment code below or ask whoever set up the console for your network.';
    case 'network_error':
      return 'Could not reach the API.';
    default:
      return 'Incorrect username, password or code.';
  }
}
